import React, { useState, useEffect } from 'react';
import { Bell, Edit, Trash2, Play, Pause, Clock, AlertCircle, TrendingUp, TrendingDown } from 'lucide-react';
import { AlertModal } from '../AlertModal/AlertModal';
import { api } from '../../utils/api';

const AlertsManagement = ({ currentUser, currentOrganization, refreshTrigger }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [locationNames, setLocationNames] = useState({});
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    if (currentUser?.email && currentOrganization?.id) {
      fetchAlerts();
      fetchLocations();
    }
  }, [currentUser, currentOrganization, refreshTrigger]);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/alerts', {
        params: {
          userId: currentUser.email,
          organizationId: currentOrganization.id
        }
      });

      // API returns { success: true, alerts: [...] }
      setAlerts(response.data?.alerts || []);
    } catch (err) {
      console.error('Error fetching alerts:', err);
      setError('Failed to load alerts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      // Fetch locations from the organization
      const response = await api.get('/locations', {
        params: {
          organizationId: currentOrganization.id
        }
      });

      if (response.data && response.data.length > 0) {
        const locations = response.data.map(loc => loc.id).sort((a, b) => a - b);
        const names = response.data.reduce((acc, loc) => {
          acc[loc.id] = loc.name;
          return acc;
        }, {});

        setAvailableLocations(locations);
        setLocationNames(names);
      }
    } catch (err) {
      console.error('Error fetching locations:', err);
      // Don't set error state as this is not critical
    }
  };

  const handleEdit = (alert) => {
    setEditingAlert(alert);
    setShowEditModal(true);
  };

  const handleDelete = async (alert) => {
    if (!window.confirm('Are you sure you want to delete this alert?')) {
      return;
    }

    const actionKey = `delete-${alert.id}`;

    try {
      setActionLoading(prev => ({ ...prev, [actionKey]: true }));
      await api.delete(`/alerts/${alert.id}`);
      fetchAlerts(); // Refresh the list
    } catch (err) {
      console.error('Error deleting alert:', err);
      setError('Failed to delete alert. Please try again.');
    } finally {
      setActionLoading(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  const handleToggleActive = async (alert) => {
    const actionKey = `toggle-${alert.id}`;
    const newStatus = !alert.isActive;

    try {
      setActionLoading(prev => ({ ...prev, [actionKey]: true }));
      await api.patch(`/alerts`, {
        id: alert.id,
        userId: alert.userId,
        isActive: newStatus
      });
      fetchAlerts(); // Refresh the list
    } catch (err) {
      console.error('Error updating alert:', err);
      setError('Failed to update alert status. Please try again.');
    } finally {
      setActionLoading(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  const handleModalClose = () => {
    setShowEditModal(false);
    setEditingAlert(null);
  };

  const handleModalSave = () => {
    setShowEditModal(false);
    setEditingAlert(null);
    fetchAlerts(); // Refresh the list
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const getMetricLabel = (metricType) => {
    const labels = {
      revenue: 'Revenue',
      occupancy: 'Occupancy',
      adr: 'ADR',
      revpar: 'RevPAR',
      totalRevenue: 'Total Revenue',
      hoursPerOccupiedRoom: 'Hours/Room',
      dollarsPerOccupiedRoom: 'Labor$/Room',
      otPercentage: 'OT %'
    };
    return labels[metricType] || metricType;
  };

  const getConditionLabel = (condition) => {
    const labels = {
      above: 'Above',
      below: 'Below',
      equals: 'Equals'
    };
    return labels[condition] || condition;
  };

  const getTimeframeLabel = (timeframe) => {
    const labels = {
      yesterday: 'Yesterday',
      last7: 'Last 7 Days',
      last30: 'Last 30 Days',
      last60: 'Last 60 Days',
      last90: 'Last 90 Days',
      thisMonth: 'This Month',
      lastMonth: 'Last Month'
    };
    return labels[timeframe] || timeframe;
  };

  const getLocationDisplay = (alert) => {
    if (!alert.locations || alert.locations.length === 0) {
      return 'No locations';
    }
    if (alert.locations.includes('all')) {
      return 'All Locations';
    }
    if (alert.locations.length === 1) {
      return locationNames[parseInt(alert.locations[0])] || `Location ${alert.locations[0]}`;
    }
    return `${alert.locations.length} locations`;
  };

  const getStatusBadge = (alert) => {
    if (!alert.isActive) {
      return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">Paused</span>;
    }
    return <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">Active</span>;
  };

  const getConditionIcon = (condition) => {
    if (condition === 'above') {
      return <TrendingUp size={16} className="text-orange-500" />;
    }
    return <TrendingDown size={16} className="text-blue-500" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading alerts...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Alert Configurations</h2>
          <p className="text-gray-600">Manage your performance alerts and notifications</p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertCircle size={20} className="text-red-500 mr-3" />
          <span className="text-red-700">{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            ×
          </button>
        </div>
      )}

      {/* Alerts List */}
      {alerts.length === 0 ? (
        <div className="text-center py-12">
          <Bell size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No alerts configured yet</h3>
          <p className="text-gray-600 mb-4">Create alerts from the Dashboard to get notified when metrics meet your conditions</p>
        </div>
      ) : (
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <div className="overflow-auto max-h-[70vh]">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Alert Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Checked
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Locations
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {alerts.map((alert) => (
                  <tr key={alert.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {getConditionIcon(alert.condition)}
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">{alert.alertName}</div>
                          <div className="text-sm text-gray-500">
                            {getMetricLabel(alert.metricType)} {getConditionLabel(alert.condition)} {alert.threshold}
                            {alert.metricType === 'occupancy' || alert.metricType === 'otPercentage' ? '%' : ''}
                            {' · '}{getTimeframeLabel(alert.timeframe)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(alert)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center text-sm text-gray-900">
                        <Clock size={16} className="mr-2 text-gray-400" />
                        {formatDate(alert.lastChecked)}
                      </div>
                      {alert.lastTriggered && (
                        <div className="text-xs text-orange-600 mt-1">
                          Triggered: {formatDate(alert.lastTriggered)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {getLocationDisplay(alert)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        {/* Toggle Status Button */}
                        <button
                          onClick={() => handleToggleActive(alert)}
                          disabled={actionLoading[`toggle-${alert.id}`]}
                          className={`p-2 rounded ${
                            alert.isActive
                              ? 'text-yellow-600 hover:bg-yellow-50'
                              : 'text-green-600 hover:bg-green-50'
                          } transition-colors disabled:opacity-50`}
                          title={alert.isActive ? 'Pause' : 'Activate'}
                        >
                          {alert.isActive ? <Pause size={16} /> : <Play size={16} />}
                        </button>

                        {/* Edit Button */}
                        <button
                          onClick={() => handleEdit(alert)}
                          className="p-2 text-gray-600 hover:bg-gray-50 rounded transition-colors"
                          title="Edit"
                        >
                          <Edit size={16} />
                        </button>

                        {/* Delete Button */}
                        <button
                          onClick={() => handleDelete(alert)}
                          disabled={actionLoading[`delete-${alert.id}`]}
                          className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit Alert Modal */}
      {showEditModal && editingAlert && (
        <AlertModal
          isOpen={showEditModal}
          onClose={handleModalClose}
          onSave={handleModalSave}
          existingAlert={editingAlert}
          userSettings={currentUser}
          currentOrganization={currentOrganization}
          availableLocations={availableLocations}
          locationNames={locationNames}
        />
      )}
    </div>
  );
};

export default AlertsManagement;
